import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Search, Download, Shield, ShieldOff, ChevronLeft, ChevronRight, Edit2, X, Save, StickyNote } from 'lucide-react';
import { api } from '../services/api';

interface Contact {
  id: string;
  name: string | null;
  email: string | null;
  notes: string | null;
  country: string | null;
  city: string | null;
  device_type: string | null;
  browser: string | null;
  os: string | null;
  utm_source: string | null;
  utm_campaign: string | null;
  language: string | null;
  is_blocked: boolean;
  blocked_at: string | null;
  blocked_reason: string | null;
  conversation_count: number;
  last_seen: string | null;
  created_at: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function Contacts() {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [blockedFilter, setBlockedFilter] = useState<string>('');
  const [exporting, setExporting] = useState(false);

  // Edit modal state
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: pagination.page,
        limit: pagination.limit,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (blockedFilter) params.blocked = blockedFilter;

      const data = await api.getContacts(params);
      setContacts(data.contacts);
      setPagination(data.pagination);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, debouncedSearch, blockedFilter]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [debouncedSearch, blockedFilter]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (blockedFilter) params.blocked = blockedFilter;

      const blob = await api.exportContacts(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `contacts-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to export contacts:', error);
      alert('Failed to export contacts');
    } finally {
      setExporting(false);
    }
  };

  const handleBlockContact = async (contactId: string) => {
    if (!confirm(t('contacts.confirmBlock'))) return;
    try {
      await api.blockContact(contactId);
      loadContacts();
    } catch (error) {
      console.error('Failed to block contact:', error);
    }
  };

  const handleUnblockContact = async (contactId: string) => {
    try {
      await api.unblockContact(contactId);
      loadContacts();
    } catch (error) {
      console.error('Failed to unblock contact:', error);
    }
  };

  const handleEditContact = (contact: Contact) => {
    setEditingContact(contact);
    setEditForm({
      name: contact.name || '',
      email: contact.email || '',
      notes: contact.notes || '',
    });
  };

  const handleSaveContact = async () => {
    if (!editingContact) return;

    setSaving(true);
    try {
      await api.updateContact(editingContact.id, editForm);
      setEditingContact(null);
      loadContacts();
    } catch (error) {
      console.error('Failed to save contact:', error);
      alert(t('contacts.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get display name - use actual name, or infer from email, or fallback to anonymous
  const getDisplayName = (contact: Contact) => {
    // If they have a real name (not "Anonymous"), use it
    if (contact.name && contact.name.toLowerCase() !== 'anonymous') {
      return contact.name;
    }
    // If they have an email, infer name from it
    if (contact.email) {
      const localPart = contact.email.split('@')[0];
      // Replace dots, underscores, hyphens with spaces and title case
      return localPart
        .replace(/[._-]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    }
    // Fallback
    return t('contacts.anonymous');
  };

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-gray-900" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('contacts.title')}</h1>
              <p className="text-gray-600">{t('contacts.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting || contacts.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
          >
            <Download className="w-4 h-4" />
            {exporting ? t('common.exporting') : t('contacts.export')}
          </button>
        </div>

        {/* Filters */}
        <div className="mt-4 flex gap-4">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('contacts.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
            />
          </div>

          {/* Blocked filter */}
          <select
            value={blockedFilter}
            onChange={(e) => setBlockedFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
          >
            <option value="">{t('contacts.allContacts')}</option>
            <option value="false">{t('contacts.activeOnly')}</option>
            <option value="true">{t('contacts.blockedOnly')}</option>
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">{t('common.loading')}</div>
          </div>
        ) : contacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Users className="w-12 h-12 mb-4 text-gray-300" />
            <p>{t('contacts.noContacts')}</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.name')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.email')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.country')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.device')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.browser')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.utmSource')}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.conversations')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.lastSeen')}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.created')}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.notes')}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('contacts.columns.status')}
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contacts.map((contact) => (
                      <tr key={contact.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="font-medium text-gray-900">
                            {getDisplayName(contact)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {contact.email || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {contact.country || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 capitalize">
                          {contact.device_type || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {contact.browser || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          {contact.utm_source || '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium text-gray-700">
                            {contact.conversation_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-sm">
                          {formatDateTime(contact.last_seen)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-sm">
                          {formatDate(contact.created_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {contact.notes ? (
                            <div className="group relative inline-block">
                              <StickyNote className="w-4 h-4 text-amber-500 cursor-pointer" />
                              <div className="hidden group-hover:block absolute z-10 bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg whitespace-pre-wrap max-w-xs">
                                {contact.notes}
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {contact.is_blocked ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              {t('contacts.blocked')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {t('contacts.active')}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleEditContact(contact)}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title={t('contacts.edit')}
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            {contact.is_blocked ? (
                              <button
                                onClick={() => handleUnblockContact(contact.id)}
                                className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition"
                                title={t('contacts.unblock')}
                              >
                                <ShieldOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => handleBlockContact(contact.id)}
                                className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                                title={t('contacts.block')}
                              >
                                <Shield className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  {t('contacts.showing', {
                    from: (pagination.page - 1) * pagination.limit + 1,
                    to: Math.min(pagination.page * pagination.limit, pagination.total),
                    total: pagination.total,
                  })}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-gray-600">
                    {t('contacts.page', { current: pagination.page, total: pagination.totalPages })}
                  </span>
                  <button
                    onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Contact Modal */}
      {editingContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{t('contacts.editContact')}</h3>
              <button
                onClick={() => setEditingContact(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contacts.columns.name')}
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder={t('contacts.namePlaceholder')}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contacts.columns.email')}
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent"
                  placeholder={t('contacts.emailPlaceholder')}
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('contacts.notes')}
                </label>
                <textarea
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                  placeholder={t('contacts.notesPlaceholder')}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200">
              <button
                onClick={() => setEditingContact(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveContact}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-400 transition"
              >
                <Save className="w-4 h-4" />
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
